import { useState, useEffect } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { useAuth, API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";
import axios from "axios";
import { 
  User,
  CreditCard,
  Building,
  Building2,
  AlertCircle,
  RefreshCw,
  Shield,
  Loader2,
  CheckCircle,
  Crown,
  Plug,
  Database,
  Globe,
  Stethoscope,
  Heart,
  Activity,
  Trash2,
  ClipboardList
} from "lucide-react";

const plans = [
  {
    id: "starter",
    name: "Starter",
    price: 49,
    pa_limit: 100,
    features: ["Up to 100 PA requests", "1 provider", "5 insurers", "Email support"]
  },
  {
    id: "growth",
    name: "Growth",
    price: 149,
    pa_limit: 500,
    features: ["Up to 500 PA requests", "5 providers", "All insurers", "RPA submission", "Analytics dashboard"],
    popular: true
  },
  {
    id: "clinic_pro",
    name: "Enterprise",
    price: 349,
    pa_limit: 999999,
    features: ["Unlimited PA requests", "Unlimited providers", "All insurers", "EHR integration", "Dedicated CSM"]
  }
];

const EHR_OPTIONS = [
  { id: "hapi_r4", name: "HAPI FHIR R4 (Public)", icon: Database, status: "available", desc: "Open FHIR R4 server for testing" },
  { id: "smart_sandbox", name: "SMART Health IT Sandbox", icon: Globe, status: "available", desc: "SMART on FHIR sandbox with OAuth" },
  { id: "epic", name: "Epic", icon: Heart, status: "coming_soon", desc: "Epic EHR integration" },
  { id: "cerner", name: "Oracle Cerner", icon: Building, status: "coming_soon", desc: "Cerner Millennium" },
  { id: "eclinicalworks", name: "eClinicalWorks", icon: ClipboardList, status: "coming_soon", desc: "eCW EHR" },
  { id: "athena", name: "Athenahealth", icon: Stethoscope, status: "coming_soon", desc: "Athena EHR" },
];

function OpenDentalCard() {
  const [developerKey, setDeveloperKey] = useState("");
  const [customerKey, setCustomerKey] = useState("");
  const [patientSyncInterval, setPatientSyncInterval] = useState("1 hour");
  const [appointmentSyncInterval, setAppointmentSyncInterval] = useState("15 minutes");
  const [coverageWindowDays, setCoverageWindowDays] = useState(7);
  const [reverificationThresholdHours, setReverificationThresholdHours] = useState(24);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [savingConfig, setSavingConfig] = useState(false);
  const [testingConn, setTestingConn] = useState(false);
  const [syncingAll, setSyncingAll] = useState(false);
  const [isConfigured, setIsConfigured] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        setLoadingConfig(true);
        const res = await axios.get(`${API}/v1/integrations/open-dental/settings`, { withCredentials: true });
        if (res.data) {
          const conn = res.data.connection || {};
          const settings = res.data.settings || {};
          setDeveloperKey(conn.developerKeyMasked || "");
          setCustomerKey(conn.customerKeyMasked || "");
          setIsConfigured(conn.is_connected || false);
          if (settings.patient_sync_interval) setPatientSyncInterval(settings.patient_sync_interval);
          if (settings.appointment_sync_interval) setAppointmentSyncInterval(settings.appointment_sync_interval);
          if (settings.coverage_window_days) setCoverageWindowDays(settings.coverage_window_days);
          if (settings.reverification_threshold_hours) setReverificationThresholdHours(settings.reverification_threshold_hours);
        }
      } catch (err) {
        console.error("Error fetching Open Dental config:", err);
      } finally {
        setLoadingConfig(false);
      }
    };
    fetchConfig();
  }, []);

  const handleSave = async (e) => {
    if (e) e.preventDefault();
    setSavingConfig(true);
    try {
      const res = await axios.put(`${API}/v1/integrations/open-dental/settings`, {
        patient_sync_interval: patientSyncInterval,
        appointment_sync_interval: appointmentSyncInterval,
        coverage_window_days: parseInt(coverageWindowDays, 10),
        reverification_threshold_hours: parseInt(reverificationThresholdHours, 10)
      }, { withCredentials: true });

      if (res.data.status === "success") {
        toast.success("Open Dental integration settings updated!");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update settings");
    } finally {
      setSavingConfig(false);
    }
  };

  const handleTestConnection = async () => {
    if (!developerKey || !customerKey) {
      toast.error("Please provide Developer Key and Customer Key.");
      return;
    }
    setTestingConn(true);
    setTestResult(null);
    try {
      const res = await axios.post(`${API}/v1/integrations/open-dental/test`, {
        developer_key: developerKey,
        customer_key: customerKey
      }, { withCredentials: true });

      if (res.data.connected) {
        setTestResult({ success: true, message: res.data.message || "Connection test successful!" });
        setIsConfigured(true);
        toast.success("Open Dental connection verified!");
      } else {
        setTestResult({ success: false, message: res.data.message || "Connection test failed." });
        toast.error(res.data.message || "Connection test failed");
      }
    } catch (err) {
      const msg = err.response?.data?.detail || "Connection test failed";
      setTestResult({ success: false, message: msg });
      toast.error(msg);
    } finally {
      setTestingConn(false);
    }
  };

  const handleFullSync = async () => {
    setSyncingAll(true);
    try {
      const res = await axios.post(`${API}/v1/integrations/open-dental/sync`, {}, { withCredentials: true });
      if (res.data.status === "completed") {
        toast.success("Full Open Dental synchronization completed!");
      } else {
        toast.success("Sync triggered!");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to sync with Open Dental");
    } finally {
      setSyncingAll(false);
    }
  };

  if (loadingConfig) {
    return <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 text-emerald-400 animate-spin" /></div>;
  }

  return (
    <Card className="bg-slate-900/50 border-slate-800 mt-6">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-white flex items-center gap-2">
              <Building2 className="w-5 h-5 text-emerald-400" /> Open Dental Integration & Synchronization
            </CardTitle>
            <CardDescription className="text-slate-400 mt-1">
              Manage API keys, synchronization intervals, coverage windows, and threshold settings.
            </CardDescription>
          </div>
          {isConfigured ? (
            <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">Connected</Badge>
          ) : (
            <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">Not Connected</Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <form onSubmit={handleSave} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Developer Key</Label>
              <Input
                type="password"
                value={developerKey}
                onChange={(e) => setDeveloperKey(e.target.value)}
                placeholder="Enter Open Dental Developer Key"
                className="bg-slate-800 border-slate-700 text-white font-mono"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Customer Key</Label>
              <Input
                type="password"
                value={customerKey}
                onChange={(e) => setCustomerKey(e.target.value)}
                placeholder="Enter Open Dental Customer Key"
                className="bg-slate-800 border-slate-700 text-white font-mono"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
            <div className="space-y-2">
              <Label className="text-slate-300">Patient Sync Frequency</Label>
              <select
                value={patientSyncInterval}
                onChange={(e) => setPatientSyncInterval(e.target.value)}
                className="w-full bg-slate-800 border border-slate-700 rounded-md p-2 text-white"
              >
                <option value="Manual">Manual</option>
                <option value="5 minutes">5 minutes</option>
                <option value="15 minutes">15 minutes</option>
                <option value="30 minutes">30 minutes</option>
                <option value="1 hour">1 hour (Default)</option>
                <option value="6 hours">6 hours</option>
                <option value="Daily">Daily</option>
              </select>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Appointment Sync Frequency</Label>
              <select
                value={appointmentSyncInterval}
                onChange={(e) => setAppointmentSyncInterval(e.target.value)}
                className="w-full bg-slate-800 border border-slate-700 rounded-md p-2 text-white"
              >
                <option value="Manual">Manual</option>
                <option value="5 minutes">5 minutes</option>
                <option value="15 minutes">15 minutes (Default)</option>
                <option value="30 minutes">30 minutes</option>
                <option value="1 hour">1 hour</option>
              </select>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Coverage Window (Days)</Label>
              <select
                value={coverageWindowDays}
                onChange={(e) => setCoverageWindowDays(e.target.value)}
                className="w-full bg-slate-800 border border-slate-700 rounded-md p-2 text-white"
              >
                <option value={1}>1 Day</option>
                <option value={3}>3 Days</option>
                <option value={7}>7 Days (Default)</option>
                <option value={14}>14 Days</option>
              </select>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Reverification Threshold (Hours)</Label>
              <select
                value={reverificationThresholdHours}
                onChange={(e) => setReverificationThresholdHours(e.target.value)}
                className="w-full bg-slate-800 border border-slate-700 rounded-md p-2 text-white"
              >
                <option value={12}>12 Hours</option>
                <option value={24}>24 Hours (Default)</option>
                <option value={48}>48 Hours</option>
                <option value={72}>72 Hours</option>
              </select>
            </div>
          </div>

          {testResult && (
            <div className={`p-3 rounded-lg border text-xs flex items-center gap-2 ${testResult.success ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300" : "bg-red-500/10 border-red-500/30 text-red-300"}`}>
              {testResult.success ? <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0" /> : <AlertCircle className="w-4 h-4 text-red-400 shrink-0" />}
              <span>{testResult.message}</span>
            </div>
          )}

          <div className="flex flex-wrap gap-3 pt-2">
            <Button type="submit" disabled={savingConfig} className="bg-emerald-600 hover:bg-emerald-500 text-white gap-2">
              {savingConfig ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />} Save Settings
            </Button>
            <Button type="button" onClick={handleTestConnection} disabled={testingConn} variant="outline" className="border-slate-700 text-slate-200 hover:bg-slate-800 gap-2">
              {testingConn ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Test Connection
            </Button>
            <Button type="button" onClick={handleFullSync} disabled={syncingAll} variant="outline" className="border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 gap-2">
              {syncingAll ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Sync Open Dental
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function EhrConnectionSettings() {
  const [ehrConn, setEhrConn] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/settings/ehr-connection`, { withCredentials: true })
      .then(res => { if (res.data?.connected) setEhrConn(res.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const connectEhr = async (ehrId) => {
    setConnecting(true);
    try {
      const res = await axios.post(`${API}/settings/ehr-connection`, { ehr_id: ehrId }, { withCredentials: true });
      setEhrConn(res.data);
      toast.success(`Connected to ${res.data.ehr_name}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Connection failed"); }
    finally { setConnecting(false); }
  };

  const disconnectEhr = async () => {
    try {
      await axios.delete(`${API}/settings/ehr-connection`, { withCredentials: true });
      setEhrConn(null);
      toast.success("EHR disconnected");
    } catch { toast.error("Failed to disconnect"); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 text-emerald-400 animate-spin" /></div>;

  return (
    <div className="space-y-6">
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2"><Plug className="w-5 h-5 text-emerald-400" /> EHR Connection</CardTitle>
          <CardDescription className="text-slate-400">Connect your organization to an EHR system via SMART on FHIR. This is a one-time setup — once connected, patient data will be available directly in the PA wizard.</CardDescription>
        </CardHeader>
        <CardContent>
          {ehrConn ? (
            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-emerald-400" />
                    </div>
                    <div>
                      <p className="font-semibold text-white">{ehrConn.ehr_name}</p>
                      <p className="text-xs text-slate-400">{ehrConn.fhir_url}</p>
                      <p className="text-xs text-slate-500 mt-1">Connected {new Date(ehrConn.connected_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <Badge className="bg-emerald-500/20 text-emerald-400">Connected</Badge>
                </div>
              </div>
              <Button variant="outline" onClick={disconnectEhr} className="border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid="disconnect-ehr-btn">
                <Trash2 className="w-4 h-4 mr-2" /> Disconnect EHR
              </Button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {EHR_OPTIONS.map((ehr) => {
                const Icon = ehr.icon;
                const available = ehr.status === "available";
                return (
                  <div key={ehr.id} className={`p-4 rounded-lg border ${available ? "border-slate-700 hover:border-emerald-500/50" : "border-slate-800 opacity-50"} transition-all`}>
                    <div className="flex items-center gap-3 mb-2">
                      <div className={`w-10 h-10 rounded-lg ${available ? "bg-emerald-500/20" : "bg-slate-800"} flex items-center justify-center`}>
                        <Icon className={`w-5 h-5 ${available ? "text-emerald-400" : "text-slate-600"}`} />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-white">{ehr.name}</p>
                        <p className="text-xs text-slate-500">{ehr.desc}</p>
                      </div>
                    </div>
                    {available ? (
                      <Button size="sm" className="w-full bg-emerald-600 hover:bg-emerald-500 text-white mt-2" onClick={() => connectEhr(ehr.id)} disabled={connecting} data-testid={`connect-ehr-${ehr.id}`}>
                        {connecting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plug className="w-4 h-4 mr-1" />} Connect
                      </Button>
                    ) : (
                      <Badge className="bg-slate-800 text-slate-500 mt-2">Coming Soon</Badge>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <OpenDentalCard />
    </div>
  );
}

export default function Settings() {
  const { user, organization, setOrganization, login } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(null);

  // Organization name (clinic name) — editable for admins
  const [clinicName, setClinicName] = useState("");
  const [savingClinic, setSavingClinic] = useState(false);
  useEffect(() => {
    setClinicName(organization?.name || "");
  }, [organization]);

  const saveClinicName = async () => {
    if (!clinicName.trim()) { toast.error("Clinic name cannot be empty"); return; }
    setSavingClinic(true);
    try {
      await axios.put(`${API}/org/profile`, { name: clinicName.trim() }, { withCredentials: true });
      // refresh org context
      const me = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setOrganization(me.data.organization || null);
      toast.success("Clinic name updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update clinic name");
    } finally { setSavingClinic(false); }
  };

  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const response = await axios.get(`${API}/subscription`, { withCredentials: true });
        setSubscription(response.data);
      } catch (error) {
        console.error("Error fetching subscription:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchSubscription();
  }, []);

  const handleUpgrade = async (planId) => {
    setUpgrading(planId);
    try {
      const orderRes = await axios.post(`${API}/subscription/create-order`, { plan: planId }, { withCredentials: true });
      await axios.post(`${API}/subscription/confirm`, { plan: planId, order_id: orderRes.data.order_id }, { withCredentials: true });
      toast.success(`Upgraded to ${planId.charAt(0).toUpperCase() + planId.slice(1)} plan!`);
      const subRes = await axios.get(`${API}/subscription`, { withCredentials: true });
      setSubscription(subRes.data);
    } catch (error) {
      toast.error("Failed to upgrade plan");
    } finally {
      setUpgrading(null);
    }
  };

  const handleStartTrial = (planId) => {
    login();
  };

  const getInitials = (name) => {
    return name?.split(" ").map(n => n[0]).join("").toUpperCase() || "U";
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-400" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="settings-page">
        <div>
          <h1 className="text-2xl font-bold text-white">Settings</h1>
          <p className="text-slate-400">Manage your account and subscription</p>
        </div>

        <Tabs defaultValue="profile" className="space-y-6">
          <TabsList className="bg-slate-800 border-slate-700">
            <TabsTrigger value="profile" className="gap-2 data-[state=active]:bg-slate-700 data-[state=active]:text-white">
              <User className="w-4 h-4" />
              Profile
            </TabsTrigger>
            <TabsTrigger value="billing" className="gap-2 data-[state=active]:bg-slate-700 data-[state=active]:text-white">
              <CreditCard className="w-4 h-4" />
              Billing
            </TabsTrigger>
            <TabsTrigger value="organization" className="gap-2 data-[state=active]:bg-slate-700 data-[state=active]:text-white">
              <Building className="w-4 h-4" />
              Organization
            </TabsTrigger>
            <TabsTrigger value="ehr" className="gap-2 data-[state=active]:bg-slate-700 data-[state=active]:text-white">
              <Plug className="w-4 h-4" />
              EHR Connection
            </TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <Card className="bg-slate-900/50 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white">Profile Information</CardTitle>
                <CardDescription className="text-slate-400">Your account details from Google Sign-In</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center gap-4">
                  <Avatar className="h-20 w-20">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="bg-emerald-500/20 text-emerald-400 text-xl">
                      {getInitials(user?.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <h3 className="text-lg font-semibold text-white">{user?.name}</h3>
                    <p className="text-slate-400">{user?.email}</p>
                    <Badge className="mt-2 bg-emerald-500/20 text-emerald-400 border-0">
                      {user?.role?.charAt(0).toUpperCase() + user?.role?.slice(1)}
                    </Badge>
                  </div>
                </div>

                <Separator className="bg-slate-700" />

                <div className="grid gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">Full Name</Label>
                    <Input value={user?.name || ""} disabled className="bg-slate-800 border-slate-700 text-slate-300" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Email</Label>
                    <Input value={user?.email || ""} disabled className="bg-slate-800 border-slate-700 text-slate-300" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">User ID</Label>
                    <Input value={user?.user_id || ""} disabled className="bg-slate-800 border-slate-700 text-slate-300 font-mono text-sm" />
                  </div>
                </div>

                <p className="text-sm text-slate-500">
                  Profile information is managed through your Google account.
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Billing Tab */}
          <TabsContent value="billing">
            <div className="space-y-6">
              <Card className="bg-slate-900/50 border-slate-800">
                <CardHeader>
                  <CardTitle className="text-white">Current Plan</CardTitle>
                  <CardDescription className="text-slate-400">Your subscription details and usage</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <Crown className="w-6 h-6 text-emerald-400" />
                        <div>
                          <h3 className="font-semibold text-emerald-400">
                            {subscription?.plan?.charAt(0).toUpperCase() + subscription?.plan?.slice(1) || 'Starter'} Plan
                          </h3>
                          <p className="text-sm text-emerald-400/70">
                            ${plans.find(p => p.id === subscription?.plan)?.price || '49'}/month
                          </p>
                        </div>
                      </div>
                      <Badge className="bg-emerald-500 text-slate-900 border-0">
                        {subscription?.status?.charAt(0).toUpperCase() + subscription?.status?.slice(1) || 'Active'}
                      </Badge>
                    </div>

                    <div className="space-y-3">
                      <div>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-slate-400">PA Requests Used</span>
                          <span className="font-medium text-white">
                            {subscription?.pa_used || 0} / {subscription?.pa_limit || 100}
                          </span>
                        </div>
                        <div className="w-full bg-slate-800 rounded-full h-2">
                          <div 
                            className="bg-emerald-500 h-2 rounded-full transition-all"
                            style={{ width: `${Math.min(100, ((subscription?.pa_used || 0) / (subscription?.pa_limit || 100)) * 100)}%` }}
                          />
                        </div>
                      </div>

                      <div className="flex justify-between text-sm">
                        <span className="text-slate-400">Current Period</span>
                        <span className="text-white">
                          {subscription?.current_period_start ? new Date(subscription.current_period_start).toLocaleDateString() : '-'} 
                          {' - '}
                          {subscription?.current_period_end ? new Date(subscription.current_period_end).toLocaleDateString() : '-'}
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-900/50 border-slate-800">
                <CardHeader>
                  <CardTitle className="text-white">Available Plans</CardTitle>
                  <CardDescription className="text-slate-400">Upgrade or change your subscription</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-3 gap-4">
                    {plans.map((plan) => {
                      const isCurrentPlan = subscription?.plan === plan.id;
                      return (
                        <div 
                          key={plan.id}
                          className={`relative p-4 rounded-xl border-2 transition-all ${
                            isCurrentPlan 
                              ? 'border-emerald-500 bg-emerald-500/10' 
                              : plan.popular 
                                ? 'border-amber-500/50 bg-amber-500/5' 
                                : 'border-slate-700 hover:border-slate-600 bg-slate-800/50'
                          }`}
                        >
                          {plan.popular && !isCurrentPlan && (
                            <Badge className="absolute -top-2 left-1/2 -translate-x-1/2 bg-amber-500 text-slate-900 border-0">
                              Most Popular
                            </Badge>
                          )}
                          {isCurrentPlan && (
                            <Badge className="absolute -top-2 left-1/2 -translate-x-1/2 bg-emerald-500 text-slate-900 border-0">
                              Current Plan
                            </Badge>
                          )}
                          
                          <h4 className="font-semibold text-white mt-2">{plan.name}</h4>
                          <div className="mt-2 mb-4">
                            <span className="text-2xl font-bold text-white">${plan.price}</span>
                            <span className="text-slate-400">/mo</span>
                          </div>
                          
                          <ul className="space-y-2 mb-4">
                            {plan.features.map((feature, idx) => (
                              <li key={idx} className="flex items-center gap-2 text-sm text-slate-300">
                                <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                                {feature}
                              </li>
                            ))}
                          </ul>

                          <div className="space-y-2">
                            <Button
                              className={`w-full ${isCurrentPlan ? 'bg-slate-700 text-slate-400' : 'bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold'}`}
                              disabled={isCurrentPlan || upgrading === plan.id}
                              onClick={() => handleUpgrade(plan.id)}
                            >
                              {upgrading === plan.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : isCurrentPlan ? (
                                "Current Plan"
                              ) : (
                                "Upgrade"
                              )}
                            </Button>
                            {!isCurrentPlan && (
                              <Button
                                variant="outline"
                                className="w-full border-slate-600 text-slate-300 hover:bg-slate-800"
                                onClick={() => handleStartTrial(plan.id)}
                              >
                                Start 7-Day Free Trial
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Organization Tab */}
          <TabsContent value="organization">
            <Card className="bg-slate-900/50 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white">Organization Settings</CardTitle>
                <CardDescription className="text-slate-400">Manage your clinic or hospital settings. The clinic name set here will appear in every Prior Authorization request.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="p-4 bg-slate-800/50 border border-slate-700 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Building className="w-8 h-8 text-slate-500" />
                    <div>
                      <h3 className="font-medium text-white">{organization?.name || `${user?.name}'s Practice`}</h3>
                      <p className="text-sm text-slate-400">Organization ID: {user?.organization_id}</p>
                    </div>
                  </div>
                </div>

                {/* Clinic name editable input */}
                <div className="space-y-2">
                  <Label className="text-white">Organization / Clinic Name <span className="text-red-400">*</span></Label>
                  <p className="text-xs text-slate-400">This name is sent on every PA request and appears in the Create PA Request form.</p>
                  <div className="flex gap-3 max-w-2xl">
                    <Input
                      value={clinicName}
                      onChange={e => setClinicName(e.target.value)}
                      placeholder="e.g., Sunrise Medical Center"
                      className="bg-slate-800 border-slate-700 text-white"
                      data-testid="settings-clinic-name"
                    />
                    <Button
                      onClick={saveClinicName}
                      disabled={savingClinic || !clinicName.trim() || clinicName.trim() === (organization?.name || "")}
                      className="bg-emerald-600 hover:bg-emerald-500 text-white"
                      data-testid="settings-clinic-save"
                    >
                      {savingClinic ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-1" />}
                      Save
                    </Button>
                  </div>
                </div>

                <Separator className="bg-slate-700" />

                <div className="space-y-4">
                  <h4 className="font-medium text-white flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    Compliance & Security
                  </h4>
                  <div className="grid gap-3">
                    <div className="flex items-center justify-between p-3 bg-slate-800/50 border border-slate-700 rounded-lg">
                      <span className="text-sm text-slate-300">HIPAA Compliance</span>
                      <Badge className="bg-emerald-500/20 text-emerald-400 border-0">Compliant</Badge>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-800/50 border border-slate-700 rounded-lg">
                      <span className="text-sm text-slate-300">Data Encryption</span>
                      <Badge className="bg-emerald-500/20 text-emerald-400 border-0">AES-256</Badge>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-800/50 border border-slate-700 rounded-lg">
                      <span className="text-sm text-slate-300">Data Residency</span>
                      <Badge className="bg-slate-700 text-slate-300 border-0">AWS US-East-1</Badge>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* EHR Connection Tab */}
          <TabsContent value="ehr">
            <EhrConnectionSettings />
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
